import React, { forwardRef } from 'react';
import { TouchableOpacity } from 'react-native';
import type { View as RNView } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useCommunity } from '@/hooks/useCommunity';
import { Card } from '@/components/gluestack/card';
import { View } from '@/components/gluestack/view';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Heading } from '@/components/gluestack/heading';
import { Text } from '@/components/gluestack/text';
import type { ProductSubscription } from 'expo-iap';

type PlanType = 'monthly' | 'yearly';

export interface IPlanCardProps {
  product: ProductSubscription;
  planType: PlanType;
  isRecommended?: boolean;
  onPress: () => void;
  isCurrentPlan?: boolean;
  isProcessing: boolean;
}

const PlanCard = forwardRef<RNView, IPlanCardProps>(
  (
    { product, planType, isRecommended, onPress, isCurrentPlan, isProcessing },
    ref
  ) => {
    const { t } = useTranslation();
    const community = useCommunity();

    const getSavingsPercent = (): number => {
      if (planType === 'yearly' && product.displayPrice) {
        return 36;
      }
      return 0;
    };

    const getMonthlyEquivalent = (): string => {
      if (planType === 'yearly') {
        const priceMatch = product.displayPrice?.match(/[\d.,]+/);
        if (priceMatch) {
          const yearlyPrice = parseFloat(priceMatch[0].replace(',', ''));
          const monthlyEquiv = (yearlyPrice / 12).toFixed(2);
          return monthlyEquiv;
        }
      }
      return '';
    };

    const savingsPercent = getSavingsPercent();
    const monthlyEquiv = getMonthlyEquivalent();

    return (
      <TouchableOpacity
        ref={ref}
        onPress={onPress}
        disabled={isCurrentPlan || isProcessing}
        activeOpacity={0.7}
      >
        <Card
          className={`relative overflow-hidden ${
            isRecommended ? 'border-2 border-primary-500' : ''
          } ${isCurrentPlan ? 'opacity-60' : ''}`}
          size="md"
        >
          {isRecommended && (
            <View className="absolute top-0 right-0 bg-primary-500 px-3 py-0.5 rounded-bl-lg">
              <Text size="xs" bold className="text-white">{t('account.plans.bestValue')}</Text>
            </View>
          )}

          <VStack space="sm" className="py-2">
            <HStack className="justify-between items-start">
              <VStack space="xs" className="flex-1">
                <Heading size="lg" className="text-typography-900">
                  {planType === 'monthly'
                    ? t('account.plans.monthlyPlan', { planName: community?.premiumPlan })
                    : t('account.plans.yearlyPlan', { planName: community?.premiumPlan })}
                </Heading>
                {planType === 'yearly' && monthlyEquiv ? (
                  <Text size="sm" className="text-typography-600">
                    {t('account.plans.onlyPerMonth', { price: monthlyEquiv, percent: savingsPercent })}
                  </Text>
                ) : (
                  <Text size="sm" className="text-typography-600">{t('account.plans.regularPrice')}</Text>
                )}
              </VStack>

              <VStack space="xs" className="items-end gap-0">
                <Heading size="xl" className="text-primary-500">
                  {product.displayPrice}
                </Heading>
                <Text size="xs" className="text-typography-500">
                  {planType === 'monthly' ? t('account.plans.perMonth') : t('account.plans.perYear')}
                </Text>
              </VStack>
            </HStack>

            {isCurrentPlan && (
              <View className="bg-success-100 px-3 py-2 rounded-lg mt-2">
                <Text className="text-success-700 font-semibold text-center">
                  {t('account.plans.currentPlan')}
                </Text>
              </View>
            )}
          </VStack>
        </Card>
      </TouchableOpacity>
    );
  }
);

export default PlanCard;




